import { Box, Button, Link, List, ListItem, Theme, Typography } from "@mui/material";
import CustomIcon from "components/CustomIcon";
import { Checkbox } from "components/Checkbox";
import { StatusCardHeader } from "../StatusCardHeader";

import { FormattedMessage, useIntl } from "react-intl";
import icons from "enums/icons";
import { IPreviousReports } from "pages/MyPace/useMyPace";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utils/hooks";

interface IMyPaceProps {
    statusLegend: string;
    statusBarColor: string;
    isRetakeAvailable: boolean;
    retakeAvailableOn: string;
    previousReportsList: IPreviousReports[];
    handleRetestPace: () => Promise<void>;
    handleUpdatePreviousList: (ev: React.ChangeEvent<HTMLInputElement>, id: number) => void;
    hasOngoingAssessment: boolean;
}

const MyAssessmentCard = ({
    statusLegend,
    statusBarColor,
    isRetakeAvailable,
    retakeAvailableOn,
    previousReportsList,
    handleRetestPace,
    handleUpdatePreviousList,
    hasOngoingAssessment,
}: IMyPaceProps) => {
    const intl = useIntl();
    const navigate = useNavigate();

    const { loggedInUserData } = useAuth();

    const navigateToAssessment = (assessmentId: number) => {
        navigate(`/pace/report/user/${loggedInUserData.userId}/assessment/${assessmentId}`);
    };

    return (
        <Box sx={{ backgroundColor: "background.paleBlue", borderRadius: "16px 16px 6px 6px", overflow: "hidden" }}>
            <StatusCardHeader
                title={statusLegend}
                backgroundColor={statusBarColor}
                icon={statusLegend === "Up to Date" ? icons.circledCheck : icons.errorTriangle}
            />

            {/* CARD BODY */}
            <Box p={2}>
                <Box display="flex" alignItems="center" mb={2}>
                    <CustomIcon icon={icons.paceIconColouredDark} sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                        {intl.formatMessage({ id: "assessmentCard.title" })}
                    </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ color: "secondary.main" }} mb={1}>
                    {intl.formatMessage({ id: "assessmentCard.description" })}
                </Typography>

                <Box
                    height={460}
                    sx={(theme: Theme) => ({
                        padding: theme.spacing(3, 1),
                        backgroundColor: "common.white",
                        borderRadius: "4px",
                    })}
                >
                    <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                        {!isRetakeAvailable ? (
                            <>
                                <CustomIcon icon={icons.paceIconColouredDark} sx={{ fontSize: 14, mr: 1 }} />
                                <Typography variant="body1" fontSize={12} letterSpacing={0.7} color="primary.main">
                                    <FormattedMessage
                                        id="retestCard.nextRetakeAvailable"
                                        values={{ nextRetake: retakeAvailableOn }}
                                    />
                                </Typography>
                            </>
                        ) : (
                            <Button variant="contained" fullWidth sx={{ marginBottom: 1 }} onClick={handleRetestPace}>
                                <CustomIcon icon={icons.paceIconColouredDark} sx={{ fontSize: 14, mr: 1 }} />
                                <Typography variant="body1" fontSize={12}>
                                    {hasOngoingAssessment
                                        ? intl.formatMessage({ id: "retestCard.button.label.continue" })
                                        : intl.formatMessage({ id: "retestCard.button.label" })}
                                </Typography>
                            </Button>
                        )}
                    </Box>
                    <List sx={{ m: 0, p: 0, maxHeight: 392, overflowY: "auto" }}>
                        {previousReportsList.map(({ id, label, subLabel, checked }) => {
                            return (
                                <ListItem
                                    key={id}
                                    disablePadding
                                    sx={{ letterSpacing: "1.25px", marginBottom: 1.2 }}
                                    secondaryAction={
                                        <Link
                                            type="button"
                                            underline="none"
                                            color="primary.main"
                                            onClick={() => navigateToAssessment(id)}
                                        >
                                            <Typography variant="caption">
                                                {intl.formatMessage({
                                                    id: "assessmentCard.content.listItem.button",
                                                })}
                                            </Typography>
                                        </Link>
                                    }
                                >
                                    <Checkbox
                                        edge="start"
                                        size="small"
                                        checked={checked}
                                        sx={{ ml: "-8px" }}
                                        onChange={(ev) => handleUpdatePreviousList(ev, id)}
                                    />
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="caption">{label}</Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            {subLabel}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
                <Box mt={2}>
                    <Typography variant="body1" fontWeight={700}>
                        {intl.formatMessage({ id: "assessmentCard.footer.title" })}
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ ul: { margin: 0, pl: 2.4 } }}>
                        {intl.formatMessage({ id: "assessmentCard.footer.list" })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default MyAssessmentCard;
